import { getOrgFromUrl, getOrgTokenFromPath } from '../utils/urlUtils';
import { getStoredOrganization, storeOrganization } from '../utils/organizationSession';
import { apiService } from './api';

export type AnonymousSession = {
  accessToken: string;
  expiresIn?: number; // seconds
  sessionId?: string;
  sub?: string;
  organization_id?: string;
  projectId?: number;
  projectName?: string | null;
};

// Derive server base URL from REACT_APP_API_URL (which usually ends with /api)
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const SERVER_BASE_URL = API_BASE_URL.replace(/\/?api\/?$/, '');

// Optional gating secret header/name (dev-only; may be unset in prod)
const ANON_SHARED_SECRET = process.env.REACT_APP_ANON_SHARED_SECRET;
const ANON_SHARED_SECRET_HEADER = process.env.REACT_APP_ANON_SHARED_SECRET_HEADER || 'X-App-Shared-Secret';

async function postJson<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
  const res = await fetch(`${SERVER_BASE_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(ANON_SHARED_SECRET ? { [ANON_SHARED_SECRET_HEADER]: ANON_SHARED_SECRET } : {}),
      ...(init?.headers || {}),
    },
    body: body ? JSON.stringify(body) : undefined,
    ...init,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const err = new Error(`${res.status} ${res.statusText}${text ? ` - ${text}` : ''}`);
    (err as any).status = res.status;
    throw err;
  }
  // Some endpoints may return no body
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) return {} as T;
  return (await res.json()) as T;
}

export async function resolveOrganizationIdForAnon(): Promise<string> {
  // Priority: URL query ?org -> URL path token (resolve via API) -> localStorage organization
  const urlOrg = getOrgFromUrl();
  if (urlOrg) return urlOrg;

  const pathToken = getOrgTokenFromPath();
  if (pathToken) {
    // Resolve token to organization via public API
    const org = await apiService.organizations.getByToken(pathToken);
    // Persist for reuse
    storeOrganization(org);
    return org.organization_id;
  }

  const stored = getStoredOrganization();
  if (stored?.organization_id) return stored.organization_id;

  throw new Error('No organization context available');
}

export async function createAnonymousSession(organization_id?: string): Promise<AnonymousSession> {
  const orgId = organization_id || (await resolveOrganizationIdForAnon());
  return await postJson<AnonymousSession>('/auth/anonymous', { organization_id: orgId });
}

export async function refreshAnonymousSession(): Promise<AnonymousSession> {
  return await postJson<AnonymousSession>('/auth/refresh');
}

export async function resetAnonymousSession(): Promise<AnonymousSession> {
  return await postJson<AnonymousSession>('/auth/reset');
}

export function getAuthHeader(token?: string): HeadersInit {
  return token ? { Authorization: `Bearer ${token}` } : {};
}
